import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty, omit } from 'lodash';
import { Icon, Avatar, InputAdornment, withStyles, createStyles } from '@material-ui/core';
import { getInitials, stringToColor } from 'ui-utils';
import SelectField from '../../SelectField';

import Option from './Option';

const getValue = (value) => {
  if (typeof value === 'object') return value.toJS().join(',');

  return value ? value.toString() : '';
};

const styles = ({ custom: { palette, fontSizes } }) =>
  createStyles({
    adornmentRoot: {
      height: 48,
      position: 'absolute',
      top: 0,
      zIndex: 1,
    },
    adornmentIcon: {
      fontSize: fontSizes.bodyS,
    },
    avatar: {
      fontWeight: 500,
      marginRight: 8,
    },
    clearIcon: {
      color: palette.colorSupportSteel,
      fontSize: fontSizes.small,
      fontWeight: 500,
      position: 'relative',
      right: 11,
    },
  });

class FormPatientSearchField extends React.Component {
  constructor(props) {
    super(props);

    this.renderClearIcon = this.renderClearIcon.bind(this);
    this.renderValueComponent = this.renderValueComponent.bind(this);

    this.state = {
      inputValue: '',
    };
  }

  handleChange = (value) => {
    const { input } = this.props;

    input.onChange(value);
  };

  handleInputChange = (value, selectedValue) => {
    const { onFilter } = this.props;
    const { inputValue } = this.state;

    if (inputValue !== value && !selectedValue) onFilter(value);

    this.setState({ inputValue: value });
  };

  renderClearIcon() {
    const { clearIconRenderer, classes } = this.props;

    if (clearIconRenderer) return clearIconRenderer;

    return <div className={classes.clearIcon}>clear</div>;
  }

  renderValueComponent(props) {
    const { valueRenderer } = this.props;
    const name = props.children ? props.children.split(' - ')[0] : '';
    const initials = getInitials(name);
    const backgroundColor = stringToColor(name);

    if (valueRenderer) return valueRenderer(props);

    return (
      <div className="Select-value">
        <Avatar style={{ fontWeight: 500, marginRight: 8, backgroundColor, height: 35, width: 35, color: 'white' }}>
          {initials}
        </Avatar>
        {props.children}
      </div>
    );
  }

  render() {
    const {
      helperText,
      optionRenderer,
      input: { value, onBlur },
      meta: { error, touched },
      classes,
      ...other
    } = this.props;
    const selectedValue = getValue(value);
    const adornmentDisplay = selectedValue ? 'none' : 'inline-flex';
    const searchIcon = (
      <InputAdornment position="start" className={classes.adornmentRoot} style={{ display: adornmentDisplay }}>
        <Icon className={classes.adornmentIcon}>search</Icon>
      </InputAdornment>
    );

    return (
      <SelectField
        clearable
        clearIconRenderer={this.renderClearIcon()}
        error={touched && !!error}
        helperText={touched && !!error ? error : helperText}
        hideArrow
        onBlur={() => onBlur(value)}
        onChange={this.handleChange}
        onInputChange={(inputValue) => this.handleInputChange(inputValue, selectedValue)}
        onSelectResetsInput={false}
        optionComponent={optionRenderer || Option}
        searchable={isEmpty(selectedValue)}
        startAdornment={searchIcon}
        value={selectedValue}
        valueComponent={this.renderValueComponent}
        {...omit(other, ['onFilter', 'optionRenderer', 'valueRenderer'])}
        // https://github.com/react-dnd/react-dnd/issues/701
        onDrop={(e) => e.preventDefault()}
      />
    );
  }
}

FormPatientSearchField.propTypes = {
  clearIconRenderer: PropTypes.node,
  helperText: PropTypes.string,
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  onFilter: PropTypes.func.isRequired,
  optionRenderer: PropTypes.func,
  options: PropTypes.array.isRequired,
  valueRenderer: PropTypes.func,
};

export default withStyles(styles)(FormPatientSearchField);
